/**
 * POC edit audit log — Matthew Lynch only via CRM_ACTIVITY_POC_ALLOWLIST.
 * Stamps lastEditedBy/lastEditedAt on the target doc and writes an auditLog row
 * (who, collection, doc id, action, time). Field values are not logged.
 */

import {
  Timestamp,
  addDoc,
  collection,
  serverTimestamp,
  type DocumentReference,
  type UpdateData,
} from 'firebase/firestore';
import { db } from './firebase';
import { isCrmActivityPocUser } from './crmActivityPocAllowlist';
import { getCrmActivitySessionId } from './crmActivitySessionId';

export const AUDIT_LOG_COLLECTION = 'auditLog';

export type CrmAuditAction = 'create' | 'update' | 'delete';

export interface CrmAuditActor {
  uid: string;
  name?: string | null;
  email?: string | null;
}

export interface CrmAuditEventInput {
  actor: CrmAuditActor;
  collection: string;
  docId: string;
  action: CrmAuditAction;
  entityType?: string;
  entityId?: string;
  changedFields?: string[];
  source?: 'client' | 'trigger' | 'agent';
  pageKey?: string;
  viewKey?: string;
}

/** Fields to merge onto a document write for POC users. */
export function lastEditedStamp(actor: CrmAuditActor): Record<string, unknown> {
  return {
    lastEditedBy: actor.uid,
    lastEditedAt: serverTimestamp(),
    lastEditedByName: actor.name || null,
    lastEditedByEmail: actor.email || null,
  };
}

export function shouldWriteCrmAudit(actor: CrmAuditActor): boolean {
  return isCrmActivityPocUser({
    email: actor.email,
    uid: actor.uid,
    id: actor.uid,
    name: actor.name,
  });
}

export async function writeCrmAuditLog(input: CrmAuditEventInput): Promise<string | null> {
  if (!shouldWriteCrmAudit(input.actor)) return null;
  if (!input.actor.uid || !input.collection || !input.docId) return null;

  const payload: Record<string, unknown> = {
    ts: serverTimestamp(),
    actorUid: input.actor.uid,
    actorName: input.actor.name || null,
    actorEmail: input.actor.email || null,
    actorType: 'user',
    collection: input.collection,
    docId: input.docId,
    action: input.action,
    entityType: input.entityType || null,
    entityId: input.entityId || null,
    changedFields: Array.isArray(input.changedFields) ? input.changedFields.slice(0, 50) : [],
    source: input.source || 'client',
    sessionId: getCrmActivitySessionId(),
    pageKey: input.pageKey || null,
    viewKey: input.viewKey || null,
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, AUDIT_LOG_COLLECTION), payload);
  return ref.id;
}

/**
 * Merge last-edited stamps into an update payload and write an audit row.
 * No-op for non-allowlisted users (returns the original patch unchanged).
 */
export async function withCrmAuditUpdate<T extends Record<string, unknown>>(
  actor: CrmAuditActor,
  meta: {
    collection: string;
    docId: string;
    action?: CrmAuditAction;
    entityType?: string;
    entityId?: string;
    changedFields?: string[];
  },
  patch: T,
): Promise<T & Record<string, unknown>> {
  if (!shouldWriteCrmAudit(actor)) return patch;
  const stamped = { ...patch, ...lastEditedStamp(actor) };
  try {
    await writeCrmAuditLog({
      actor,
      collection: meta.collection,
      docId: meta.docId,
      action: meta.action || 'update',
      entityType: meta.entityType,
      entityId: meta.entityId,
      changedFields: meta.changedFields || Object.keys(patch).slice(0, 50),
    });
  } catch (err) {
    console.error('[CrmAudit] Failed to write auditLog row:', err);
  }
  return stamped;
}

/** Convenience: stamp + audit after a known write already succeeded. */
export async function recordCrmAuditAfterWrite(input: CrmAuditEventInput): Promise<void> {
  try {
    await writeCrmAuditLog(input);
  } catch (err) {
    console.error('[CrmAudit] Failed to write auditLog row:', err);
  }
}

/** Type helper for UpdateData merges that include audit stamps. */
export type WithLastEdited<T> = T & {
  lastEditedBy?: string;
  lastEditedAt?: ReturnType<typeof serverTimestamp> | Timestamp;
};

export async function stampDocRefUpdate(
  ref: DocumentReference,
  actor: CrmAuditActor,
  meta: { collection: string; entityType?: string; entityId?: string; changedFields?: string[] },
  patch: UpdateData<Record<string, unknown>>,
): Promise<UpdateData<Record<string, unknown>>> {
  return withCrmAuditUpdate(
    actor,
    { collection: meta.collection, docId: ref.id, entityType: meta.entityType, entityId: meta.entityId, changedFields: meta.changedFields },
    patch as Record<string, unknown>,
  ) as Promise<UpdateData<Record<string, unknown>>>;
}
